/**
 * SQL block rendered inside the Supabase connect tutorial (step 3).
 * Lives in its own module so the data-in catalog remains focused on provider
 * metadata rather than embedding a large SQL program.
 *
 * Contract:
 *  - `{{webhookUrl}}` is replaced at render time with the freshly
 *    minted `iac_<nanoid>` endpoint URL (see
 *    `app/(app)/integrations/components/tutorial-steps.tsx`
 *    `renderCodeValue`). Everything else stays literal so the
 *    operator knows where to splice their own secret.
 *  - The HMAC target MUST match the verifier at
 *    `lib/integrations/inbound/providers/supabase/verify.ts` — both
 *    hash the canonical JSONB→text bytes that pg_net POSTs.
 *  - Re-running the whole block after rotating the secret is safe:
 *    every statement is idempotent (Vault upsert, `create or replace`,
 *    `drop trigger if exists`).
 *
 * Per-statement notes live inside the SQL itself so the operator
 * reading the block in the Brew UI gets the rationale without having
 * to flip back to this module.
 */
export const SUPABASE_SETUP_SQL = `-- Brew Supabase integration — installs an HMAC-signed webhook on auth.users.
-- Before running: create your own random signing secret, then replace
-- <PASTE_YOUR_SECRET_HERE>. Brew and Supabase do not issue this secret.
-- The block is idempotent — re-run safely after rotating the secret.

-- (1) Enable required extensions. pgcrypto is relocatable; pg_net is
--     not (Supabase pins it to the "net" schema), so it gets no
--     "with schema" clause.
create extension if not exists pgcrypto with schema extensions;
create extension if not exists pg_net;

-- (2) Store the shared secret in Supabase Vault. Inserts on first run,
--     updates on subsequent runs — plaintext never lands in
--     pg_stat_statements either way.
do $$
declare
  v_id uuid;
begin
  select id into v_id from vault.secrets where name = 'brew_webhook_secret';
  if v_id is null then
    perform vault.create_secret(
      '<PASTE_YOUR_SECRET_HERE>',
      'brew_webhook_secret',
      'Brew inbound webhook signing secret'
    );
  else
    perform vault.update_secret(v_id, '<PASTE_YOUR_SECRET_HERE>');
  end if;
end $$;

-- (3) Brew helper schema + trigger function. Builds the canonical
--     {type, schema, table, record, old_record} envelope Brew's
--     extractor expects, HMAC-signs the EXACT same bytes that pg_net
--     POSTs on the wire, and fires the request fire-and-forget.
create schema if not exists brew;

create or replace function brew.notify_auth_users_change()
returns trigger
language plpgsql
security definer
set search_path = public, extensions, vault, net
as $func$
declare
  v_secret    text;
  v_body      jsonb;
  v_signature text;
  v_url constant text := '{{webhookUrl}}';
begin
  select decrypted_secret into v_secret
    from vault.decrypted_secrets
    where name = 'brew_webhook_secret'
    limit 1;
  if v_secret is null then
    raise exception
      'Vault entry "brew_webhook_secret" is missing. Re-run step (2).';
  end if;

  v_body := jsonb_build_object(
    'type',       TG_OP,
    'schema',     TG_TABLE_SCHEMA,
    'table',      TG_TABLE_NAME,
    'record',     case when TG_OP = 'DELETE' then null else to_jsonb(NEW) end,
    'old_record', case when TG_OP = 'INSERT' then null else to_jsonb(OLD) end
  );

  -- Base64(HMAC-SHA-256) over the exact JSONB->text bytes that pg_net
  -- will serialize on the wire. Matches Brew's verifier at
  -- lib/integrations/inbound/providers/supabase/verify.ts.
  v_signature := encode(
    extensions.hmac(v_body::text::bytea, v_secret::bytea, 'sha256'),
    'base64'
  );

  perform net.http_post(
    url     := v_url,
    body    := v_body,
    headers := jsonb_build_object(
      'Content-Type',         'application/json',
      'X-Supabase-Signature', v_signature
    )
  );

  return null;
end;
$func$;

-- (4) Attach the trigger to auth.users for INSERT, UPDATE, DELETE.
drop trigger if exists brew_notify_auth_users on auth.users;
create trigger brew_notify_auth_users
  after insert or update or delete on auth.users
  for each row execute function brew.notify_auth_users_change();
`
